/* eslint-disable no-nested-ternary */
import { Icon } from '@components';
import { useNavigateWithOfflineMode, useScreenSize } from '@hooks';
import CoursesImage from '@pngs/MainPage/CoursesImage.png';
import GetStartedFrameImage from '@pngs/MainPage/GetStartedFrameImage.png';
import IntroImage from '@pngs/MainPage/IntroBackground.png';
import IntroImageMobile from '@pngs/MainPage/IntroMobile.png';
import HeaderImage from '@pngs/MainPage/MainPageHeader.png';
import NetBackground from '@pngs/MainPage/MainPageNet.png';
import { courseApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as GetStartedZebra } from '@svgs/GetStartedZebra.svg';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { mainPageLayoutByVariant } from './MainPage.layoutProps';

const MainPage = () => {
  const { i18n } = useTranslation();
  const { navigate } = useNavigateWithOfflineMode();
  const { device, isMobile, isTabletLG } = useScreenSize();

  const layout = mainPageLayoutByVariant[device || 'desktop'];

  const { courses, refetch } = courseApi.useGetCourseStructureQuery(undefined, {
    selectFromResult: ({ data }) => ({
      courses: data?.data?.courses,
    }),
  });

  useEffect(() => {
    refetch();
  }, [i18n.language]);

  const navigateStartLearning = () => {
    navigate('courses');
  };

  const navigateToCourse = (courseId) => () => {
    navigate(`courses/${courseId}`);
  };

  return (
    <>
      <Flex
        backgroundImage={HeaderImage}
        backgroundRepeat="no-repeat"
        backgroundSize="cover"
        flexDirection="column"
        pb={121}
        pt={layout?.title.pt}
        px={layout?.px}
      >
        <Typography
          color={theme.colors.white[100]}
          mb={40}
          style={{ letterSpacing: '-1px' }}
          variant={layout.title.titleVariant}
        >
          {i18n.t('MainPage.WelcomePartOne')}
          <Typography
            color={theme.colors.schoolBusYellow[100]}
            display="inline"
            variant={layout.title.titleVariant}
          >
            {i18n.t('MainPage.LOAC')}
          </Typography>
          {i18n.t('MainPage.WelcomePartTwo')}
          &shy;
          {i18n.t('MainPage.WelcomePartThree')}
        </Typography>
        <Typography
          color={theme.colors.white[100]}
          variant={layout.title.subtitleVariant}
          width="80%"
        >
          {i18n.t('MainPage.WelcomeSubtitle')}
        </Typography>
      </Flex>
      <Flex
        backgroundImage={NetBackground}
        backgroundRepeat="no-repeat"
        backgroundSize="contain"
        flexDirection="column"
        width="100%"
      >
        <Flex
          {...(!isMobile && {
            backgroundImage: IntroImage,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
          })}
          justifyContent={isMobile ? 'flex-start' : 'flex-end'}
          pb={layout?.intro.pb}
          pt={layout?.intro.pt}
          px={layout?.intro.px}
        >
          <Flex
            flexDirection="column"
            width={isMobile ? '100%' : isTabletLG ? '50%' : '35%'}
          >
            <Typography
              color={theme.colors.white[100]}
              textAlign="right"
              variant={layout?.intro.titleVariant}
            >
              {i18n.t('MainPage.IntroTitle')}
            </Typography>
            <Typography
              color={theme.colors.white[60]}
              textAlign={isMobile ? 'left' : 'right'}
              variant="MS-regular-s16-lh25"
            >
              {i18n.t('MainPage.Intro')}
            </Typography>
            {isMobile && <Image src={IntroImageMobile} />}
          </Flex>
        </Flex>
        <Flex
          alignItems="center"
          backgroundImage={GetStartedFrameImage}
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          position="relative"
          px={layout.getStarted.px}
          py={isMobile ? 41 : 89}
        >
          <Flex position="absolute" right={0} top={-16} zIndex={10}>
            <Icon height={32} SVG={GetStartedZebra} width={153} />
          </Flex>
          <Flex flexDirection="column" gap={16}>
            <Typography
              color={theme.colors.white[100]}
              variant={layout?.getStarted.titleVariant}
            >
              {i18n.t('MainPage.StartLearning.Title')}
            </Typography>
            <Typography
              color={theme.colors.white[100]}
              variant={layout?.getStarted.subtitleVariant}
            >
              {i18n.t('MainPage.StartLearning.Subtitle')}
            </Typography>
          </Flex>
          <Button
            mt={isMobile ? 48 : 0}
            onClick={navigateStartLearning}
            variant="primary"
            width={340}
          >
            <Typography
              color={theme.colors.background}
              variant="MS-semibold-s16-lh17"
            >
              {i18n.t('MainPage.StartLearning.NavigationButton')}
            </Typography>
          </Button>
        </Flex>
        <Flex
          backgroundImage={CoursesImage}
          backgroundPosition={isMobile ? '120px 100%' : 'right'}
          backgroundRepeat="no-repeat"
          backgroundSize="contain"
          pb={layout?.structure.pb}
          pt={layout?.structure.pt}
          px={layout?.structure.px}
        >
          <Flex flexDirection="column" gap={isMobile ? 32 : 40}>
            <Flex
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? 32 : 80}
            >
              {courses?.map((course) => (
                <Flex flexDirection="column" justifyContent="space-between">
                  <Typography
                    color={theme.colors.white[100]}
                    mb={22}
                    textAlign="left"
                    variant={layout?.course.titleVariant}
                  >
                    {course?.title}
                  </Typography>
                  <Flex
                    borderLeftColor={theme.colors.schoolBusYellow[100]}
                    borderLeftStyle="solid"
                    borderLeftWidth={1}
                    flexDirection="column"
                    gap={isMobile ? 8 : 24}
                    pl={24}
                  >
                    {course?.modules?.map((item, index) => (
                      <Typography
                        key={`${index}-${item?.id}`}
                        color={theme.colors.white[60]}
                        variant={layout?.course.subtitleVariant}
                      >
                        {item?.title}
                      </Typography>
                    ))}
                  </Flex>
                  <Button
                    m={50}
                    mt={30}
                    onClick={navigateToCourse(course?.id)}
                    variant="primary"
                    width={200}
                  >
                    <Typography
                      color={theme.colors.background}
                      variant="MS-semibold-s16-lh17"
                    >
                      {i18n.t('MainPage.StartLearning.NavigationButton')}
                    </Typography>
                  </Button>
                </Flex>
              ))}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
};

export default MainPage;
